import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { ControlA11y } from '../../guidance/GuidedField';

/**
 * An ordered list of short statements the person writes (a role's essential duties, or its additional responsibilities): add, edit in place,
 * remove, reorder with the Up / Down buttons or with Alt+ArrowUp / Alt+ArrowDown on an item, and move an item to the other list (Essential <-> Additional).
 *
 * Suggestions (from the role's EPN template) sit in their own block with an editable text and an explicit Add button; nothing is added for the person.
 * A suggestion that was removed (or never added) comes back with "Restore removed suggestions". What is typed in the add box is never lost: it is added when
 * the box loses focus, so moving on to the next step keeps it.
 *
 * The array order is the person's order and is what is saved.
 */

export interface ReorderListProps {
  /** Wires the add box to the field's label, hint and error; `data-field` makes the box the target of "go to this field" links. */
  a11y: ControlA11y;
  /** Singular, lower case: "essential duty". */
  noun: string;
  items: string[];
  onChange: (items: string[]) => void;
  /** Moves an item to the other list. */
  moveTo?: { label: string; onMove: (index: number) => void } | undefined;
  suggestions?: string[] | undefined;
  suggestionSource?: string | undefined;
  disabled?: boolean | undefined;
}

export function ReorderList({ a11y, noun, items, onChange, moveTo, suggestions = [], suggestionSource, disabled = false }: ReorderListProps) {
  const uid = useId();
  const [draft, setDraft] = useState('');
  const [announce, setAnnounce] = useState('');
  const [edits, setEdits] = useState<Record<number, string>>({});
  const focusAfter = useRef<{ id: string } | null>(null);
  const have = new Set(items.map((i) => i.trim().toLowerCase()));
  const missing = suggestions.filter((s) => !have.has(s.trim().toLowerCase()));
  const [suggestionText, setSuggestionText] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!focusAfter.current) return;
    document.getElementById(focusAfter.current.id)?.focus();
    focusAfter.current = null;
  });

  function addLines(text: string) {
    const added = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).filter((l) => !have.has(l.toLowerCase()));
    if (added.length === 0) return;
    onChange([...items, ...added]);
    setAnnounce(added.length === 1 ? `Added ${added[0]}.` : `Added ${added.length} items.`);
  }

  function commitDraft() {
    if (!draft.trim()) {
      setDraft('');
      return;
    }
    addLines(draft);
    setDraft('');
  }

  function move(index: number, delta: -1 | 1, focus: 'up' | 'down' | 'input') {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onChange(next);
    focusAfter.current = { id: `${uid}-${focus}-${target}` };
    setAnnounce(`Moved ${moved} to position ${target + 1} of ${items.length}.`);
  }

  function remove(index: number) {
    const next = items.filter((_, i) => i !== index);
    onChange(next);
    setAnnounce(`Removed ${items[index]}.`);
    focusAfter.current = { id: next.length > 0 ? `${uid}-input-${Math.min(index, next.length - 1)}` : a11y.id };
  }

  function commitEdit(index: number) {
    const text = (edits[index] ?? items[index] ?? '').trim();
    setEdits((prev) => { const copy = { ...prev }; delete copy[index]; return copy; });
    if (!text) { remove(index); return; }
    if (text !== items[index]) {
      const next = [...items];
      next[index] = text;
      onChange(next);
    }
  }

  function onItemKey(e: KeyboardEvent<HTMLInputElement>, index: number) {
    if (e.altKey && e.key === 'ArrowUp') { e.preventDefault(); commitEdit(index); move(index, -1, 'input'); }
    else if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); commitEdit(index); move(index, 1, 'input'); }
    else if (e.key === 'Enter') { e.preventDefault(); commitEdit(index); }
  }

  return (
    <div className="jr-reorder">
      {items.length > 0 && (
        <ol className="jr-reorder__list" aria-label={`${noun[0]!.toUpperCase()}${noun.slice(1)} list, in order`}>
          {items.map((item, i) => (
            <li key={`${i}-${item}`} className="jr-reorder__item">
              <span className="jr-reorder__n" aria-hidden="true">{i + 1}.</span>
              <input
                id={`${uid}-input-${i}`} type="text" className="jr-reorder__input" disabled={disabled} maxLength={500}
                value={edits[i] ?? item}
                aria-label={`${noun[0]!.toUpperCase()}${noun.slice(1)} ${i + 1} of ${items.length}`}
                aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
                onChange={(e) => setEdits((prev) => ({ ...prev, [i]: e.target.value }))}
                onBlur={() => { if (edits[i] !== undefined) commitEdit(i); }}
                onKeyDown={(e) => onItemKey(e, i)}
              />
              <span className="jr-reorder__buttons">
                <button id={`${uid}-up-${i}`} type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled || i === 0} onClick={() => move(i, -1, 'up')}>
                  <span aria-hidden="true">{'↑'}</span><span className="jr-sr-only">Move up: {item}</span>
                </button>
                <button id={`${uid}-down-${i}`} type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled || i === items.length - 1} onClick={() => move(i, 1, 'down')}>
                  <span aria-hidden="true">{'↓'}</span><span className="jr-sr-only">Move down: {item}</span>
                </button>
                {moveTo && (
                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled} onClick={() => moveTo.onMove(i)}>
                    <span aria-hidden="true">{moveTo.label}</span><span className="jr-sr-only">{moveTo.label}: {item}</span>
                  </button>
                )}
                <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled} onClick={() => remove(i)}>
                  <span aria-hidden="true">Remove</span><span className="jr-sr-only">Remove: {item}</span>
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {items.length === 0 && <p className="jr-hint">Nothing added yet.</p>}

      <div className="jr-reorder__add">
        <label htmlFor={a11y.id} className="jr-reorder__add-label">Add {/^[aeiou]/i.test(noun) ? 'an' : 'a'} {noun}</label>
        <textarea
          id={a11y.id} data-field={a11y['data-field']} rows={2} value={draft} disabled={disabled}
          aria-describedby={a11y['aria-describedby']} aria-invalid={a11y['aria-invalid']}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); commitDraft(); } }}
        />
        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled} onClick={commitDraft}>Add</button>
        <p className="jr-hint">One per line. Use the Add button or Ctrl+Enter; whatever you have typed is also added when you leave the box.</p>
      </div>

      {suggestions.length > 0 && missing.length > 0 && (
        <div className="jr-reorder__suggestions" role="group" aria-label="Suggested items from the template">
          <p className="jr-suggestion__title">Suggested from {suggestionSource ?? 'the template'}</p>
          <p className="jr-hint">Nothing here is added until you choose Add. You can change the wording first.</p>
          <ul className="jr-plain-list jr-reorder__suggestion-list">
            {missing.map((s, i) => (
              <li key={s} className="jr-reorder__item">
                <input
                  type="text" className="jr-reorder__input" value={suggestionText[s] ?? s} disabled={disabled} maxLength={500}
                  aria-label={`Suggested ${noun} ${i + 1}: edit before adding`}
                  onChange={(e) => setSuggestionText((prev) => ({ ...prev, [s]: e.target.value }))}
                />
                <button
                  type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled}
                  onClick={() => { addLines(suggestionText[s] ?? s); setSuggestionText((prev) => { const copy = { ...prev }; delete copy[s]; return copy; }); }}
                >
                  <span aria-hidden="true">Add</span><span className="jr-sr-only">Add suggested {noun}: {s}</span>
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled}
            onClick={() => { onChange([...items, ...missing]); setAnnounce(`Restored ${missing.length} suggested ${missing.length === 1 ? 'item' : 'items'}.`); }}
          >
            Restore removed suggestions ({missing.length})
          </button>
        </div>
      )}

      <p className="jr-sr-only" role="status" aria-live="polite">{announce}</p>
    </div>
  );
}
